class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this.querySelector('.search-drawer');
    this.overlay = this.querySelector('.search-drawer__overlay');
    this.closeButton = this.querySelector('.search-drawer__close');
    this.searchInput = this.querySelector('.search-drawer__input');
    this.searchForm = this.querySelector('.search-drawer__form');
    this.suggestionsContainer = this.querySelector('.search-drawer__suggestions');
    this.popularContent = this.querySelector('.search-drawer__popular-content');

    this.searchDelay = null;
    this.currentQuery = '';
    this.selectedIndex = -1;
    this.suggestions = [];

    this.init();
  }

  init() {
    if (!this.drawer || !this.overlay) {
      console.warn('SearchDrawer: Missing required elements');
      return;
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Search input events
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleInput(e));
      this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e));
      this.searchInput.addEventListener('focus', () => this.handleFocus());
    }

    // Form submit
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  handleInput(e) {
    const query = e.target.value.trim();
    this.currentQuery = query;

    // Clear previous timeout
    clearTimeout(this.searchDelay);

    // Hide/show popular content
    if (query.length > 0) {
      this.drawer.classList.add('has-search-query');
      if (this.popularContent) {
        this.popularContent.style.display = 'none';
      }

      // Debounce search
      this.searchDelay = setTimeout(() => {
        this.fetchSuggestions(query);
      }, 300);
    } else {
      this.drawer.classList.remove('has-search-query');
      if (this.popularContent) {
        this.popularContent.style.display = 'block';
      }
      this.hideSuggestions();
    }
  }

  handleKeydown(e) {
    if (!this.suggestionsContainer || this.suggestions.length === 0) return;

    const isOpen = this.suggestionsContainer.classList.contains('is-visible');

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
        this.updateSelection();
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection();
        break;

      case 'Enter':
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          const selectedItem = this.suggestions[this.selectedIndex];
          if (selectedItem && selectedItem.url) {
            window.location.href = selectedItem.url;
          }
        } else {
          this.searchForm.submit();
        }
        break;

      case 'Escape':
        this.hideSuggestions();
        break;
    }
  }

  handleFocus() {
    if (this.currentQuery.length > 0 && this.suggestions.length > 0) {
      this.showSuggestions();
    }
  }

  handleSubmit(e) {
    // Let form submit naturally
    // The action will be to /search?q=query
  }

  async fetchSuggestions(query) {
    if (query.length < 2) {
      this.hideSuggestions();
      return;
    }

    try {
      // Show loading state
      this.showLoading();

      // Shopify predictive search API
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][fields]=title,product_type,variants.title,vendor,tag,body`
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();

      // Process results
      this.suggestions = [];

      if (data.resources && data.resources.results) {
        if (data.resources.results.products) {
          this.suggestions = data.resources.results.products.map(product => ({
            type: 'product',
            title: product.title,
            url: product.url,
            image: product.image,
            price: product.price,
            compare_at_price: product.compare_at_price,
            vendor: product.vendor,
            available: product.available
          }));
        }
      }

      // Render suggestions
      this.renderSuggestions();

    } catch (error) {
      console.error('Search error:', error);
      this.hideSuggestions();
    }
  }

  showLoading() {
    if (!this.suggestionsContainer) return;

    this.suggestionsContainer.innerHTML = `
      <div class="search-drawer__loading">
        <div class="search-drawer__spinner"></div>
        <p>Searching...</p>
      </div>
    `;
    this.suggestionsContainer.classList.add('is-visible');
  }

  renderSuggestions() {
    if (!this.suggestionsContainer) return;

    if (this.suggestions.length === 0) {
      this.suggestionsContainer.innerHTML = `
        <div class="search-drawer__no-results">
          <p>No products found for "${this.currentQuery}"</p>
          <button type="button" class="search-drawer__view-all">View all results</button>
        </div>
      `;
      this.suggestionsContainer.classList.add('is-visible');

      // Add click handler for view all
      const viewAllBtn = this.suggestionsContainer.querySelector('.search-drawer__view-all');
      if (viewAllBtn) {
        viewAllBtn.addEventListener('click', () => {
          this.searchForm.submit();
        });
      }
      return;
    }

    let html = '<div class="search-drawer__suggestions-list">';

    this.suggestions.forEach((item, index) => {
      const priceHTML = this.formatPrice(item.price, item.compare_at_price);
      const imageHTML = item.image
        ? `<img src="${item.image}" alt="${item.title}" loading="lazy">`
        : '';

      html += `
        <a href="${item.url}" 
           class="search-drawer__suggestion-item" 
           data-index="${index}"
           tabindex="0">
          <div class="search-drawer__suggestion-image">
            ${imageHTML}
          </div>
          <div class="search-drawer__suggestion-info">
            <h4 class="search-drawer__suggestion-title">${item.title}</h4>
            ${item.vendor ? `<p class="search-drawer__suggestion-vendor">${item.vendor}</p>` : ''}
            <div class="search-drawer__suggestion-price">${priceHTML}</div>
            ${!item.available ? '<span class="search-drawer__suggestion-sold-out">Sold out</span>' : ''}
          </div>
        </a>
      `;
    });

    html += '</div>';

    // Add "View all results" footer
    html += `
      <div class="search-drawer__suggestions-footer">
        <button type="submit" class="search-drawer__view-all-btn">
          View all results for "${this.currentQuery}"
        </button>
      </div>
    `;

    this.suggestionsContainer.innerHTML = html;
    this.suggestionsContainer.classList.add('is-visible');

    // Reset selection
    this.selectedIndex = -1;
  }

  formatPrice(price, compareAtPrice) {
    const formatMoney = (cents) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents);
    };

    const currentPrice = formatMoney(price);

    if (compareAtPrice && compareAtPrice > price) {
      const originalPrice = formatMoney(compareAtPrice);
      return `
        <span class="search-drawer__price-compare">MRP: ${originalPrice}</span>
        <span class="search-drawer__price-current">MRP: ${currentPrice}</span>
      `;
    }

    return `<span class="search-drawer__price-current">MRP: ${currentPrice}</span>`;
  }

  updateSelection() {
    const items = this.suggestionsContainer.querySelectorAll('.search-drawer__suggestion-item');

    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('is-selected');
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        item.classList.remove('is-selected');
      }
    });
  }

  showSuggestions() {
    if (this.suggestionsContainer) {
      this.suggestionsContainer.classList.add('is-visible');
    }
  }

  hideSuggestions() {
    if (this.suggestionsContainer) {
      this.suggestionsContainer.classList.remove('is-visible');
      this.suggestionsContainer.innerHTML = '';
    }
    this.suggestions = [];
    this.selectedIndex = -1;
  }

  open() {
    if (!this.drawer) return;

    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.classList.add('search-drawer-open');

    // Reset state
    if (this.searchInput) {
      this.searchInput.value = '';
      this.currentQuery = '';
    }

    this.drawer.classList.remove('has-search-query');

    if (this.popularContent) {
      this.popularContent.style.display = 'block';
    }

    this.hideSuggestions();

    // Focus search input
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 300);
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('search-drawer-open');

    // Clear search
    if (this.searchInput) {
      this.searchInput.value = '';
      this.currentQuery = '';
    }

    this.drawer.classList.remove('has-search-query');

    if (this.popularContent) {
      this.popularContent.style.display = 'block';
    }

    this.hideSuggestions();
  }
}

customElements.define('search-drawer', SearchDrawer);

// Initialize search drawer triggers
document.addEventListener('DOMContentLoaded', function () {
  const searchDrawer = document.querySelector('search-drawer');

  if (!searchDrawer) {
    console.warn('SearchDrawer element not found');
    return;
  }

  // Find all search trigger links
  const searchLinks = document.querySelectorAll(
    '.header__icons a[href*="/search"], .js-show-search, .js-show-search-left, [data-open-search]'
  );

  searchLinks.forEach(function (link) {
    link.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      searchDrawer.open();
      return false;
    });
  });
});